import { Button, DatePicker, message, Modal, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import { useLocalStorage } from '../localstorage';
import { get_eventsgnet_list } from '../services/api.service';
import { formatdateYYYYMMDD, MLEvent } from '../types';
import './eventsgnet.scss';
import { LeftCircleFilled, RightCircleFilled, CopyOutlined } from '@ant-design/icons';

/* -------------------------------------------------------------------------------- *\
|                               MLEventMeeting
\* -------------------------------------------------------------------------------- */
class MLEventMeeting
{
    public meetingdate: string = "";
    public sic: string = "";
    public events: MLEvent[] = [];
}
/* -------------------------------------------------------------------------------- *\
|                               DlgContent
\* -------------------------------------------------------------------------------- */
class DlgContent
{
    public content: string = "";
    public error: string = "";
}
/* -------------------------------------------------------------------------------- *\
|                               EventsGNETPage
\* -------------------------------------------------------------------------------- */
export const EventsGNETPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [lastselecteddate, setlastselecteddate] = useLocalStorage('lastgnetdate', formatdateYYYYMMDD(new Date()));
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [currentdate, setCurrentDate] = useState<string>(lastselecteddate);
    const [events, setEvents] = useState<MLEvent[]>([]);
    const [daypicker, setDaypicker] = useState<Dayjs>(dayjs(lastselecteddate));
    const [lasttime, setLastTime] = useState<string>("0");
    const [meetings, setMeetings] = useState<MLEventMeeting[]>([]);
    const [dlgvisible, setDlgvisible] = useState(false);
    const [dlgcontent, setDlgContent] = useState<DlgContent>(new DlgContent());

    var g_inrefresh: boolean = false;
    /* -------------------------------------------------------------------------------- *\
    |                               refreshEvents
    \* -------------------------------------------------------------------------------- */
    const refreshEvents = async (currentdate: string) =>
    {
        if (g_inrefresh) return Promise.resolve();

        g_inrefresh = true;
        return get_eventsgnet_list(currentdate, lasttime).then((newevents: MLEvent[]) =>
        {
            newevents = newevents.filter(ne => events.find(e => e.created === ne.created) === undefined);
            if (newevents.length > 0)
            {
                const now: number = new Date().getTime() - (lasttime === "0" ? 30000 : 0);
                for(const e of newevents)
                {
                    e.date = now;
                }

                const ees = [...newevents, ...events];
                setEvents(ees);

                const siclist: string[] = Array.from(new Set(ees.map(e => e.sic))).sort((a, b) => a.localeCompare(b));
                setMeetings(siclist.map(sic => ({ meetingdate: currentdate, sic: sic, events: ees.filter(e => e.sic === sic) })));

                setLastTime(newevents[0].filename);
            }
            else
            {
                const ms = [...meetings];
                setMeetings(ms);
            }
            g_inrefresh = false;
            return Promise.resolve();
        })
        .catch(error =>
        {
            g_inrefresh = false;
            message.error(error.message);
            if (error.status === 403 || error.status === 401) navigate('/login');
            return Promise.resolve();
        });
    }
    /* -------------------------------------------------------------------------------- *\
    |                               changeCurrentDate
    \* -------------------------------------------------------------------------------- */
    const changeCurrentDate = (date: string) =>
    {
        setEvents([]);
        setMeetings([]);
        setLastTime("0");
        setCurrentDate(date);
        setDaypicker(dayjs(date));
        setlastselecteddate(date);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               datepicker_handler
    \* -------------------------------------------------------------------------------- */
    function datepicker_handler(date: any)
    {
        changeCurrentDate(date.format('YYYYMMDD'));
    }
    /* -------------------------------------------------------------------------------- *\
    |                               formattime
    \* -------------------------------------------------------------------------------- */
    function formattime(time: string)
    {
        let t = time.substring(8);
        return t.substring(0, 2) + ":" + t.substring(2, 4) + ":" + t.substring(4, 6) + "." + t.substring(6);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               showContent
    \* -------------------------------------------------------------------------------- */
    function showContent(event: MLEvent)
    {
        setDlgContent({content: event.content, error: event.error});
        setDlgvisible(true);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               copyToClipboard
    \* -------------------------------------------------------------------------------- */
    async function copyToClipboard(content: string)
    {
        try
        {
            await navigator.clipboard.writeText(content);
            message.info('Xml copied to clipboard');
        }
        catch (err)
        {
            message.error('Copy to clipboard failed');
        }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               effects
    \* -------------------------------------------------------------------------------- */
    useEffect(() => {
        refreshEvents(currentdate).then(() => setLoading(false));
        setLoading(true);
        // eslint-disable-next-line
    }, [currentdate]);

    useEffect(() => {
        const interval = setInterval(() =>
        {
            refreshEvents(currentdate);
        }, 1000);
        return () => clearInterval(interval);

        // eslint-disable-next-line
    }, [currentdate, lasttime, events]);

    useEffect(() => {
    }, [events]);

    const now: number = new Date().getTime();

    return (
        <LayoutSite>
            <div className="eventsgnet">
                {loading ? <Loader /> : <></>}
                <div className='eventsgnet__wrapper'>
                    <div className="eventsgnet__header">
                        <div></div>
                    </div>
                    <div className='eventsgnet__calendar'>
                        <div className='eventsgnet__calendar__selector'>
                            <div className='eventsgnet__label'>Choix du jour :</div>
                            <LeftCircleFilled className='eventsgnet__calendar__arrow' onClick={() => changeCurrentDate(dayjs(currentdate).subtract(1, 'day').format('YYYYMMDD'))} />
                            <DatePicker defaultValue={dayjs(currentdate)} value={daypicker} onChange={datepicker_handler} format={'DD/MM/YYYY'} />
                            <RightCircleFilled className='eventsgnet__calendar__arrow' onClick={() => changeCurrentDate(dayjs(currentdate).add(1, 'day').format('YYYYMMDD'))}/>
                        </div>
                        <Button type="primary" onClick={() => changeCurrentDate(formatdateYYYYMMDD(new Date()))}>Aujourd'hui</Button>
                    </div>
                </div>
                <div className="eventsgnet__body">
                    <div className="eventsgnet__body__title">Events GNET</div>
                    <div className="eventsgnet__body__meetings">
                        {
                            meetings.map((m: MLEventMeeting, imeeting: number) =>
                            {
                                return (
                                    <div className={'eventsgnet__body__meeting'} key={'meeting_' + imeeting}>
                                        <div className='eventsgnet__body__meeting__title'>{m.sic}</div>
                                        {
                                            m.events.map((e: MLEvent, ievent: number) =>
                                            {
                                                const recent: boolean = (now - e.date) < 15000;
                                                return (
                                                    <div className={'eventsgnet__body__meeting__event' + (e.error ? ' eventsgnet__body__meeting__event__error' : '')} key={'event_' + ievent} onClick={() => showContent(e) }>
                                                        <div className={'eventsgnet__body__meeting__event__line' + (recent ? ' eventsgnet__body__meeting__event__recent' : '')}>
                                                            <div>{formattime(e.created)}</div>
                                                            <div>race {e.race}</div>
                                                            <div>{e.type}</div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                )   
                            })

                        }
                    </div>
                </div>
            </div>
            {
                dlgvisible &&
                <Modal width={800} className='eventsgnet__modal' title="XML content" open={dlgvisible} onOk={() => setDlgvisible(false)} onCancel={() => setDlgvisible(false)}
                    footer={[<div className='eventsgnet__modal__footer'><Tooltip title="Copy to clipboard"><Button onClick={() => copyToClipboard(dlgcontent.content)}><CopyOutlined/></Button></Tooltip><Button key="cancel" onClick={() => setDlgvisible(false)}>Close</Button></div>]}>
                    <div className="eventsgnet__modal__content">
                        {
                            dlgcontent.content &&
                            <div className="eventsgnet__modal__content__content">
                            {
                                dlgcontent.content.split('\n').map((line: string, index: number) =>
                                {
                                    return (
                                        <div key={'line_' + index}>{line}</div>
                                    )
                                })
                            }
                            </div>
                        }
                        {
                            dlgcontent.error &&
                            <div>
                                <div className="eventsgnet__modal__content__errortitle">Error</div>
                                <div className="eventsgnet__modal__content__error">{dlgcontent.error}</div>
                            </div>
                        }
                    </div>
                </Modal>
            }
        </LayoutSite>
    );
}
